import { useCallback } from 'react';
import { useHistory } from 'react-router';

import {
	trackNotificationFolderOpened, trackNotificationOpened, trackNotificationProjectOpened
} from 'src/services/tracker/trackers/notifications';

import Notification from '@notifications/models/Notification';
import NotificationFilter from '@notifications/models/NotificationFilter';
import useFetchProjectNotificationsQuery from '@notifications/queries/useFetchProjectNotificationsQuery';
import useMarkNotificationAsClickedMutation from '@notifications/queries/useMarkNotificationAsClickedMutation';

import { ESTIMATED_ROW_HEIGHT, heightForNotification } from './NotificationListItem/NotificationListItem';
import { SKELETON_ROW_HEIGHT } from './NotificationSkeleton/NotificationSkeleton';

const LOADING_SKELETON_COUNT = 3;

type UseNotificationsListParams = {
	projectID: number;
	selectedFilter: NotificationFilter;
};

const itemKeyExtractor = ( item: Notification ) => item.id;

const useNotificationsList = ( { projectID, selectedFilter }: UseNotificationsListParams ) => {
	const {
		data: notifications,
		hasNextPage,
		fetchNextPage,
		isLoading,
		isFetchingNextPage
	} = useFetchProjectNotificationsQuery( { projectID, selectedFilter } );

	const isFiltering = selectedFilter.id !== NotificationFilter.none().id;
	const loadingSkeletonCount = isLoading ? LOADING_SKELETON_COUNT : 0;
	const history = useHistory();
	const { mutate: markNotificationAsClicked } = useMarkNotificationAsClickedMutation();

	const onScrollEndReached = useCallback( () => {
		if ( hasNextPage && !isLoading && !isFetchingNextPage ) {
			fetchNextPage();
		}
	}, [ hasNextPage, isLoading, isFetchingNextPage, fetchNextPage ] );

	const itemHeight = useCallback( ( index: number ) => {
		if ( isLoading ) { return SKELETON_ROW_HEIGHT; }

		return notifications?.[ index ]
			? heightForNotification( notifications[ index ] )
			: ESTIMATED_ROW_HEIGHT
	}, [ isLoading, notifications ] );

	const onNotificationClicked = useCallback(
		( notification: Notification ) => {
			trackNotificationOpened( notification );
			markNotificationAsClicked( { notification } );

			if ( notification.relatedObjectUrl ) {
				history.replace( notification.relatedObjectUrl );
			}
		},
		[ history ]
	);

	const onNotificationProjectClicked = useCallback(
		( notification: Notification ) => {
			trackNotificationProjectOpened( notification );
			history.replace( notification.projectUrl, { onOpen: true } );
		},
		[ history ]
	);

	const onNotificationFolderClicked = useCallback(
		( notification: Notification ) => {
			trackNotificationFolderOpened( notification );
			history.replace( notification.folderUrl, { onOpen: true } );
		},
		[ history ]
	);

	return {
		notifications,
		isFiltering,
		isLoading,
		isFetchingNextPage,
		loadingSkeletonCount,
		itemHeight,
		onScrollEndReached,
		itemKeyExtractor,
		onNotificationClicked,
		onNotificationProjectClicked,
		onNotificationFolderClicked
	};
};

export default useNotificationsList;
